import { defineStore, storeToRefs } from 'pinia';
import { computed } from 'vue';
import { getCurrentUserCoachLink, getPlan } from '../browser/db/coach';
import { getSubscription } from '../browser/db/subscription';
import { getUser } from '../browser/db/user';
import type { User } from '../models';
import type { Coach } from '../models/coach';
import type { CoachLink } from '../models/coach-link';
import type { CoachPlan } from '../models/coach-plan';
import type { Subscription } from '../models/subscription';
import { EmptyArrayCheck, defineCacheStore } from './cache';

export interface CoachLinkWithDetails extends CoachLink {
  coach: Coach;
  user: User;
  plan: CoachPlan;
  subscription: Subscription;
}
const useCachedCoachStore = defineCacheStore<CoachLinkWithDetails[]>(
  'coach-cache',
  [],
  async () => {
    const links = await getCurrentUserCoachLink();
    return Promise.all(
      links.map(async (link) => {
        const { coach, plan } = await getPlan(link.coachId, link.planId);
        return {
          ...link,
          coach,
          user: await getUser(link.coachId),
          subscription: await getSubscription(link.subscriptionId),
          plan,
        } as CoachLinkWithDetails;
      })
    );
  },
  EmptyArrayCheck
);

export const useCoachStore = defineStore('coach', () => {
  const cachedStore = useCachedCoachStore();
  const { cache: links } = storeToRefs(cachedStore);
  const { loadAndUpdateCache } = cachedStore;

  const isFreemium = computed(() => {
    return links.value.some((link) => link.freemium);
  });

  async function clearCoachData() {
    links.value = [];
    cachedStore.clearCache();
  }

  return {
    links,
    loadAndUpdateCache,
    isFreemium,
    clearCoachData,
  };
});
